import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Room {
  id: number;
  name: string;
  room_type: string;
  capacity: number;
  daily_rate: number;
  is_active: number;
  current_occupancy?: number;
}

interface Booking {
  id: number;
  pet_id: number;
  pet_name: string;
  owner_name: string;
  room_id: number;
  room_name: string;
  check_in_date: string;
  check_out_date: string;
  status: string;
  daily_rate: number;
  total_amount: number;
  amount_paid: number;
  special_instructions?: string;
}

interface Pet {
  id: number;
  name: string;
  client_id: number;
}

interface Stats {
  currently_boarded: number;
  upcoming_bookings: number;
  departing_today: number;
  arriving_today: number;
  total_collected: number;
  total_billed: number;
}

const BoardingPage = () => {
  const [tab, setTab] = useState(0);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deleteBooking, setDeleteBooking] = useState<Booking | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    room_type: 'standard',
    capacity: 1,
    daily_rate: 0,
  });
  const [bookingForm, setBookingForm] = useState({
    pet_id: '',
    room_id: '',
    check_in_date: '',
    check_out_date: '',
    special_instructions: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [roomsRes, bookingsRes, petsRes, statsRes] = await Promise.all([
        api.get<{ data: Room[] }>(API_ENDPOINTS.BOARDING.LIST),
        api.get<{ data: Booking[] }>(API_ENDPOINTS.BOARDING.LIST),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
        api.get<Stats>(API_ENDPOINTS.BOARDING.LIST),
      ]);
      setRooms(roomsRes.data ?? []);
      setBookings(bookingsRes.data ?? []);
      setPets(petsRes.data ?? []);
      setStats(statsRes);
    } catch (err) {
      setError('Failed to load boarding data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    try {
      await api.post(API_ENDPOINTS.BOARDING.LIST, formData);
      setRoomDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Failed to create room');
      console.error(err);
    }
  };

  const handleUpdateRoom = async () => {
    if (!editingRoom) return;
    try {
      await api.put(`${API_ENDPOINTS.BOARDING.LIST}/${editingRoom.id}`, formData);
      setRoomDialogOpen(false);
      setEditingRoom(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Failed to update room');
      console.error(err);
    }
  };

  const handleCreateBooking = async () => {
    try {
      await api.post(API_ENDPOINTS.BOARDING.LIST, {
        ...bookingForm,
        pet_id: parseInt(bookingForm.pet_id),
        room_id: parseInt(bookingForm.room_id),
      });
      setBookingDialogOpen(false);
      resetBookingForm();
      fetchData();
    } catch (err) {
      setError('Failed to create booking');
      console.error(err);
    }
  };

  const handleCheckIn = async (bookingId: number) => {
    try {
      await api.post(API_ENDPOINTS.BOARDING.CHECKIN(bookingId));
      fetchData();
    } catch (err) {
      setError('Failed to check in');
      console.error(err);
    }
  };

  const handleCheckOut = async (bookingId: number) => {
    try {
      await api.post(API_ENDPOINTS.BOARDING.CHECKOUT(bookingId));
      fetchData();
    } catch (err) {
      setError('Failed to check out');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', room_type: 'standard', capacity: 1, daily_rate: 0 });
  };

  const resetBookingForm = () => {
    setBookingForm({ pet_id: '', room_id: '', check_in_date: '', check_out_date: '', special_instructions: '' });
  };

  const openEditRoom = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      room_type: room.room_type,
      capacity: room.capacity,
      daily_rate: room.daily_rate,
    });
    setRoomDialogOpen(true);
  };

  const handleDeleteBooking = async () => {
    if (!deleteBooking) return;
    setDeleting(true);
    try {
      await api.delete(API_ENDPOINTS.BOARDING.DELETE(deleteBooking.id));
      setDeleteBooking(null);
      fetchData();
    } catch { setError('Failed to delete booking'); } finally { setDeleting(false); }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reserved': return 'info';
      case 'checked_in': return 'success';
      case 'checked_out': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Boarding Management</Typography>
      
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Currently Boarded</Typography>
              <Typography variant="h4">{stats.currently_boarded}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Upcoming</Typography>
              <Typography variant="h4">{stats.upcoming_bookings}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Arriving Today</Typography>
              <Typography variant="h4">{stats.arriving_today}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Departing Today</Typography>
              <Typography variant="h4">{stats.departing_today}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Bookings" />
        <Tab label="Rooms" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBookingDialogOpen(true)}>
              New Booking
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pet</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Check In</TableCell>
                  <TableCell>Check Out</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map(booking => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.pet_name}</TableCell>
                    <TableCell>{booking.owner_name}</TableCell>
                    <TableCell>{booking.room_name}</TableCell>
                    <TableCell>{booking.check_in_date}</TableCell>
                    <TableCell>{booking.check_out_date}</TableCell>
                    <TableCell>
                      <Chip label={booking.status.replace('_', ' ')} color={getStatusColor(booking.status) as any} size="small" />
                    </TableCell>
                    <TableCell>${(booking.total_amount ?? 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {booking.status === 'Booked' && (
                        <Button size="small" onClick={() => handleCheckIn(booking.id)}>Check In</Button>
                      )}
                      {booking.status === 'Checked In' && (
                        <Button size="small" onClick={() => handleCheckOut(booking.id)}>Check Out</Button>
                      )}
                      {booking.status !== 'Checked In' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteBooking(booking)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {bookings.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center">No bookings found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setRoomDialogOpen(true); }}>
              Add Room
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Daily Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rooms.map(room => (
                  <TableRow key={room.id}>
                    <TableCell>{room.name}</TableCell>
                    <TableCell>{room.room_type}</TableCell>
                    <TableCell>{room.capacity}</TableCell>
                    <TableCell>${room.daily_rate.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={room.is_active ? 'Active' : 'Inactive'} color={room.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openEditRoom(room)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rooms.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">No rooms found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={roomDialogOpen} onClose={() => setRoomDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Name" value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Room Type</InputLabel>
            <Select value={formData.room_type} label="Room Type"
              onChange={e => setFormData({ ...formData, room_type: e.target.value })}>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="deluxe">Deluxe</MenuItem>
              <MenuItem value="icu">ICU</MenuItem>
              <MenuItem value="isolation">Isolation</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Capacity" type="number" value={formData.capacity}
            onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })} />
          <TextField fullWidth margin="normal" label="Daily Rate" type="number" value={formData.daily_rate}
            onChange={e => setFormData({ ...formData, daily_rate: parseFloat(e.target.value) || 0 })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDialogOpen(false)}>Cancel</Button>
          <Button onClick={editingRoom ? handleUpdateRoom : handleCreateRoom} variant="contained">
            {editingRoom ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Booking</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Pet</InputLabel>
            <Select value={bookingForm.pet_id} label="Pet"
              onChange={e => setBookingForm({ ...bookingForm, pet_id: e.target.value })}>
              {pets.map(pet => <MenuItem key={pet.id} value={pet.id}>{pet.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Room</InputLabel>
            <Select value={bookingForm.room_id} label="Room"
              onChange={e => setBookingForm({ ...bookingForm, room_id: e.target.value })}>
              {rooms.filter(r => r.is_active).map(room => (
                <MenuItem key={room.id} value={room.id}>{room.name} - ${room.daily_rate}/day</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Check In Date" type="date" value={bookingForm.check_in_date}
            onChange={e => setBookingForm({ ...bookingForm, check_in_date: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="normal" label="Check Out Date" type="date" value={bookingForm.check_out_date}
            onChange={e => setBookingForm({ ...bookingForm, check_out_date: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="normal" label="Special Instructions" multiline rows={3}
            value={bookingForm.special_instructions}
            onChange={e => setBookingForm({ ...bookingForm, special_instructions: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBooking} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!deleteBooking} onClose={() => setDeleteBooking(null)}>
        <DialogTitle>Delete Booking</DialogTitle>
        <DialogContent>
          <Typography>Delete boarding stay for <strong>{deleteBooking?.pet_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBooking(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteBooking} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoardingPage;
